import { useState, useMemo, useEffect } from "react";
import {
  ArrowLeft,
  Maximize2,
  Gauge,
  Plane,
  Wind,
  Info,
  ChevronLeft,
  ChevronRight,
  X,
  ShieldCheck,
  Clock,
  CreditCard,
  Zap,
  CheckCircle2,
  Calendar,
  PlaneTakeoff,
  Settings,
  MapPin
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import InstrumentLoader from "@/components/InstrumentLoader";
import { message, Tooltip } from "antd";
import AppConst from "../AppConst";
import AircraftRentalAvailabilityModal from "@/components/AircraftRentalAvailabilityModal";
import { AircraftRentalDto, AircraftRentalDetailsDto } from "@/types";

const isInvalidValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim().toLowerCase();
  return str === "" || str === "null" || str === "undefined" || str === "n/a" || str === "na";
};

const RentalProductDetails = () => {
  const { params, navigateTo } = useNavigation();
  const rentalId = params?.rentalId;
  const aircraftRentalFromParams = params?.aircraftRental as (AircraftRentalDto & Partial<AircraftRentalDetailsDto>) | undefined;
  const seller = params?.seller;

  const { aircraftRentals, loading, getAircraftRentalDetails } = useAppContext();
  const { isAuthenticated, myAircraftRentals } = useAuth();

  const [details, setDetails] = useState<AircraftRentalDetailsDto | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const isSeller = useMemo(() => {
    const isSellerParam = Number(seller) === 1 || seller === "true" || seller === true;

    const targetId = rentalId || aircraftRentalFromParams?.rentalId;
    const isInMyRentals = myAircraftRentals && targetId
      ? myAircraftRentals.some((myRental: any) => Number(myRental.rentalId) === Number(targetId))
      : false;

    return !!(isSellerParam || isInMyRentals);
  }, [seller, rentalId, aircraftRentalFromParams, myAircraftRentals]);

  const baseRental = useMemo(() => {
    if (aircraftRentalFromParams) return aircraftRentalFromParams;
    if (!rentalId) return null;
    return aircraftRentals.find(r => Number(r.rentalId) === Number(rentalId)) || null;
  }, [aircraftRentals, rentalId, aircraftRentalFromParams]);

  useEffect(() => {
    const targetId = Number(rentalId || baseRental?.rentalId);
    if (!targetId) {
      setIsLoadingDetails(false);
      return;
    }

    let isMounted = true;
    setIsLoadingDetails(true);

    getAircraftRentalDetails(targetId)
      .then((data) => {
        if (isMounted && data) {
          setDetails(data);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingDetails(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [rentalId, baseRental?.rentalId, getAircraftRentalDetails]);

  const rental = useMemo(() => {
    if (!baseRental) return null;
    return {
      ...baseRental,
      ...(details || {})
    };
  }, [baseRental, details]);

  const billingDefinitions: Record<string, React.ReactNode> = {
    HOBBS: (
      <div className="font-mono tracking-tighter leading-tight">
        <span className="text-white">MSG RDY:</span> ÎNREGISTREAZĂ TIMPUL <span className="text-blue-400">REAL</span> DE FUNCȚIONARE (CONTACT ON/OFF). UTILIZAT PENTRU <span className="text-white">BILLING</span> TOTAL.
      </div>
    ),
    TACHO: (
      <div className="font-mono tracking-tighter leading-tight">
        <span className="text-white">MSG RDY:</span> TIMP BAZAT PE <span className="text-blue-400">RPM</span> MOTOR. COST <span className="text-white">REDUS</span> PENTRU TAXIING ȘI STARE LA SOL.
      </div>
    ),
    WET: (
      <div className="font-mono tracking-tighter leading-tight">
        <span className="text-white">FUEL MSG:</span> TARIFUL INCLUDE <span className="text-blue-400">COMBUSTIBIL</span> ȘI LUBRIFIANȚI. <span className="text-white">ALL INCLUSIVE</span>.
      </div>
    ),
    DRY: (
      <div className="font-mono tracking-tighter leading-tight">
        <span className="text-white">FUEL MSG:</span> TARIFUL INCLUDE DOAR <span className="text-blue-400">AERONAVA</span>. COMBUSTIBILUL SE ACHITĂ <span className="text-white">SEPARAT</span>.
      </div>
    )
  };

  const handleAction = () => {
    if (isSeller) {
      navigateTo("rent-aircraft", { aircraftRental: rental });
      return;
    }

    if (!isAuthenticated) {
      message.warning("Autentificarea este necesară pentru a programa o vizionare.");
      navigateTo("login");
      return;
    }
    setIsBookingModalOpen(true);
  };

  const images = useMemo(() => {
    if (rental?.mediaUrls && rental.mediaUrls.length > 0) {
      return rental.mediaUrls.filter(url => url && url.trim() !== "");
    }

    if (rental?.media && rental.media.length > 0) {
      return rental.media.map((m: any) => {
        const data = m.mediaData;
        if (!data) return "";

        if (data.startsWith("iVBOR")) return `data:image/png;base64,${data}`;
        if (data.startsWith("/9j/")) return `data:image/jpeg;base64,${data}`;

        return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
      }).filter((img: string) => img !== "");
    }

    return [`https://via.placeholder.com/1200x800?text=${encodeURIComponent(rental?.model || "Rental")}`];
  }, [rental]);

  const DetailRow = ({ label, value, icon: Icon }: { label: string; value?: string | number | boolean | null; icon?: any }) => {
    if (typeof value !== "boolean" && isInvalidValue(value)) return null;

    return (
      <div className="flex justify-between items-center py-3 border-b border-white/5 group hover:bg-white/[0.02] px-2 transition-colors">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-500" />}
          <span className="text-slate-400 text-[11px] font-bold uppercase tracking-wider">{label}</span>
        </div>
        <span className="text-white font-mono text-sm text-right">
          {typeof value === "boolean" ? (value ? "DA" : "NU") : value}
        </span>
      </div>
    );
  };

  const isPageLoading = (loading && !aircraftRentalFromParams) || isLoadingDetails;

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <InstrumentLoader visible={true} />
      </div>
    );
  }

  if (!rental) return (
    <div className="text-white text-center py-20 font-sans min-h-screen bg-[#020617]">
      <p className="mb-4 text-slate-500 uppercase tracking-widest font-black">Fleet Unit Not Found</p>
      <button onClick={() => navigateTo("rentals")} className="text-blue-500 underline font-bold">
        Return to Fleet Ops
      </button>
    </div>
  );

  const hasCruiseSpeed = !isInvalidValue(rental.cruiseSpeed);
  const hasFuelConsumption = !isInvalidValue(rental.fuelConsumption);
  const hasAvionics = rental.avionics && rental.avionics.length > 0;
  const hasEquipment = rental.equipment && rental.equipment.length > 0;
  const hasLicences = rental.requiredLicenceTypes && rental.requiredLicenceTypes.length > 0;
  const hasAdditionalDetails = !isInvalidValue(rental.additionalDetails);

  return (
    <div className="container py-8 text-white min-h-screen lg:h-screen flex flex-col lg:overflow-hidden font-sans bg-[#020617]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 flex-1 lg:overflow-hidden">
        <div className="lg:col-span-7 h-auto lg:h-full lg:overflow-y-auto no-scrollbar pr-0 lg:pr-2">
          <div className="flex flex-row lg:flex-col rounded-[2rem] overflow-x-auto lg:overflow-hidden snap-x snap-mandatory no-scrollbar border border-white/10 bg-slate-900 shadow-2xl">
            {images.map((src, idx) => (
              <div
                key={idx}
                className="relative min-w-full lg:min-w-0 snap-center group cursor-zoom-in border-r lg:border-r-0 lg:border-b border-white/5 last:border-0"
                onClick={() => {
                  setPhotoIndex(idx);
                  setIsLightboxOpen(true);
                }}
              >
                <img
                  src={src}
                  className="w-full h-[350px] md:h-[500px] lg:h-auto object-cover transition-transform duration-500 lg:group-hover:scale-[1.01]"
                  alt={`Aircraft ${idx}`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/1200x800?text=Image+Unavailable';
                  }}
                />

                <div className="hidden lg:flex absolute inset-0 bg-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center">
                  <Maximize2 className="text-white" size={32} />
                </div>

                <div className="lg:hidden absolute bottom-4 right-6 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-mono border border-white/10 text-white">
                  {idx + 1} / {images.length}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 h-auto lg:h-full lg:overflow-y-auto no-scrollbar pb-10 lg:pb-20">
          <div className="bg-[#0F172A]/80 backdrop-blur-xl p-8 rounded-[2.5rem] border border-slate-800 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/10 blur-3xl -z-10" />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-blue-600 text-[9px] font-black rounded uppercase tracking-tighter">Anunț activ</span>
                {!isInvalidValue(rental.registration) && (
                  <span className="text-slate-500 font-mono text-[10px] tracking-widest">{rental.registration}</span>
                )}
              </div>
              <h1 className="text-4xl font-black tracking-tighter mb-1 uppercase italic">
                {rental.manufacturer} <span className="text-slate-500">{rental.model}</span>
              </h1>
              <p className="text-slate-400 text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                <PlaneTakeoff size={14} className="text-emerald-500" /> {rental.category || "General"} {!isInvalidValue(rental.baseAirport) && `• ${rental.baseAirport}`}
              </p>
            </header>

            <div className="bg-slate-950/50 border border-slate-800 p-6 rounded-2xl mb-8">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tarif orar</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-white italic">
                      {rental.grossValue && Number(rental.grossValue) > 0 ? `€${rental.grossValue}` : "LA CERERE"}
                    </span>
                    {!isInvalidValue(rental.pricingType) && (
                      <Tooltip
                        title={billingDefinitions[rental.pricingType!.toUpperCase()] || "UNIT DATA MSG"}
                        overlayInnerStyle={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          fontSize: '11px',
                          lineHeight: '1.4',
                          color: '#22c55e',
                          backgroundColor: 'rgba(0, 20, 0, 0.85)',
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          borderRadius: '4px',
                          padding: '12px',
                          textTransform: 'uppercase'
                        }}
                      >
                        <span className="text-blue-500 font-bold uppercase text-xs cursor-help transition-colors hover:text-blue-300">
                          / {rental.pricingType}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
                {!isInvalidValue(rental.billingMethod) && (
                  <div className="text-right">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Metodă de plată</p>
                    <Tooltip
                      title={billingDefinitions[rental.billingMethod!.toUpperCase()] || "UNIT DATA MSG"}
                      overlayInnerStyle={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: '11px',
                        lineHeight: '1.4',
                        color: '#22c55e',
                        backgroundColor: 'rgba(0, 20, 0, 0.85)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: '4px',
                        padding: '12px',
                        textTransform: 'uppercase'
                      }}
                    >
                      <p className="text-white font-mono font-bold text-sm uppercase cursor-help transition-colors hover:text-orange-400">
                        {rental.billingMethod}
                      </p>
                    </Tooltip>
                  </div>
                )}
              </div>
              {!(isSeller && rental.verified === false) && (
                <button
                  className="w-full py-4 bg-[#f26c15] hover:bg-[#df510d] text-white font-black rounded-xl shadow-lg shadow-black/40 transition-all flex items-center justify-center gap-3 active:scale-[0.98] uppercase tracking-widest text-sm"
                  onClick={handleAction}
                >
                  {isSeller ? (
                    <>
                      <Settings size={18} /> Editează Anunțul
                    </>
                  ) : (
                    "Solicită Rezervare"
                  )}
                </button>
              )}
            </div>

            <div className="space-y-10">
              <section>
                <h3 className="text-blue-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                  <Zap size={14} /> Performanță & Telemetrie
                </h3>
                {(hasCruiseSpeed || hasFuelConsumption) && (
                  <div className={`grid ${hasCruiseSpeed && hasFuelConsumption ? "grid-cols-2" : "grid-cols-1"} gap-4 mb-4`}>
                    {hasCruiseSpeed && (
                      <div className="bg-slate-950/40 p-4 rounded-xl border border-white/5">
                        <Wind size={16} className="text-slate-500 mb-2" />
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Viteză de croazieră</p>
                        <p className="text-xl font-black text-white">{rental.cruiseSpeed} <span className="text-xs text-slate-600">KTS</span></p>
                      </div>
                    )}
                    {hasFuelConsumption && (
                      <div className="bg-slate-950/40 p-4 rounded-xl border border-white/5">
                        <Zap size={16} className="text-slate-500 mb-2" />
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Consum</p>
                        <p className="text-xl font-black text-white">{rental.fuelConsumption} <span className="text-xs text-slate-600">L/H</span></p>
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-1">
                  <DetailRow label="Tip de combustibil" value={rental.fuelType} />
                  <DetailRow label="Certificare IFR" value={rental.isIfrCertified} />
                  <DetailRow label="An de fabricație" value={rental.productionYear} />
                  <DetailRow label="Status de mentenanță" value={!isInvalidValue(rental.lastMaintenanceCheck) ? `Ultima inspecție: ${rental.lastMaintenanceCheck}` : null} />
                </div>
              </section>

              <section>
                <h3 className="text-blue-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                  <ShieldCheck size={14} /> Cerințe privind echipajul
                </h3>
                <div className="bg-slate-950/40 p-5 rounded-2xl border border-blue-500/10 space-y-3">
                  <DetailRow label="Experiență minimă necesară" value={!isInvalidValue(rental.minimumTotalExperience) ? `${rental.minimumTotalExperience} h` : null} />
                  <DetailRow label="Evaluare practică necesară" value={rental.checkOutRequired} />
                  {hasLicences && (
                    <div>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Calificări & Licențe necesare</p>
                      <div className="flex flex-wrap gap-2">
                        {rental.requiredLicenceTypes?.filter(lic => !isInvalidValue(lic)).map((lic, i) => (
                          <span key={i} className="px-3 py-1 bg-blue-600/10 text-blue-400 border border-blue-500/20 rounded-lg text-[10px] font-black">
                            {lic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {(!isInvalidValue(rental.insurance) || !isInvalidValue(rental.insuranceDeductible)) && (
                <section>
                  <h3 className="text-blue-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                    <CreditCard size={14} /> Asigurare
                  </h3>
                  <div className="space-y-1">
                    <DetailRow label="Tip de asigurare" value={rental.insurance} />
                    <DetailRow label="Deductibil" value={!isInvalidValue(rental.insuranceDeductible) ? `€${rental.insuranceDeductible}` : null} />
                  </div>
                </section>
              )}

              {(hasAvionics || hasEquipment) && (
                <section>
                  <h3 className="text-blue-500 text-[10px] uppercase tracking-[0.3em] font-black mb-4 flex items-center gap-2">
                    <Info size={14} /> Dotări & Echipamente
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {rental.avionics?.filter(item => !isInvalidValue(item)).map((item, idx) => (
                      <span key={`av-${idx}`} className="px-2 py-1 bg-orange-600/10 text-orange-400 border border-orange-500/20 rounded text-[10px] font-bold uppercase tracking-tighter">
                        {item}
                      </span>
                    ))}
                    {rental.equipment?.filter(item => !isInvalidValue(item)).map((item, idx) => (
                      <span key={`eq-${idx}`} className="px-2 py-1 bg-white/5 text-slate-300 border border-white/10 rounded text-[10px] font-bold uppercase">
                        {item}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {hasAdditionalDetails && (
                <section className="pt-6 border-t border-slate-800">
                  <h3 className="text-slate-500 text-[10px] uppercase tracking-[0.3em] font-black mb-3">Detalii operaționale</h3>
                  <p className="text-slate-400 text-xs leading-relaxed italic">
                    {rental.additionalDetails}
                  </p>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      {isLightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center animate-in fade-in duration-300" onClick={() => setIsLightboxOpen(false)}>
          <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center pointer-events-none">
            <span className="text-white font-mono text-sm pointer-events-auto">{photoIndex + 1} / {images.length}</span>
            <button className="p-3 hover:bg-white/10 rounded-full transition-colors pointer-events-auto" onClick={() => setIsLightboxOpen(false)}>
              <X size={32} />
            </button>
          </div>
          <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex((p) => (p === 0 ? images.length - 1 : p - 1));
              }}
              className="absolute left-4 md:left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all active:scale-90 z-10"
            >
              <ChevronLeft size={40} />
            </button>
            <div
              className="w-full max-w-[85vw] aspect-video rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 bg-slate-900"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[photoIndex]}
                className="w-full h-full object-cover"
                alt="Gallery View"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/1200x800?text=Image+Unavailable';
                }}
              />
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex((p) => (p + 1) % images.length);
              }}
              className="absolute right-4 md:right-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all active:scale-90 z-10"
            >
              <ChevronRight size={40} />
            </button>
          </div>
        </div>
      )}

      <AircraftRentalAvailabilityModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        registration={rental?.registration || ""}
        hourlyRate={rental?.grossValue || 0}
        intervalStep={30}
        rentalDetails={rental}
      />
    </div>
  );
};

export default RentalProductDetails;